import React, { useRef } from 'react';

import Popup from '../../../common/Popup/Popup.module';
import type { RdvItem } from '../types';

interface EmailConfirmDialogProps {
  isOpen: boolean;
  submitting: boolean;
  rdv: RdvItem | null;
  onClose: () => void;
  onConfirm: () => void;
  renderEmailPreviewForRdv: (rdv: RdvItem, type: 'client' | 'collaborateur') => React.ReactNode;
  onCopyHtml: (ref: React.RefObject<HTMLDivElement>) => Promise<void>;
}

function EmailConfirmDialog({ isOpen, submitting, rdv, onClose, onConfirm, renderEmailPreviewForRdv, onCopyHtml }: EmailConfirmDialogProps) {
  const clientPreviewRef = useRef<HTMLDivElement | null>(null);
  const collabPreviewRef = useRef<HTMLDivElement | null>(null);

  return (
    <Popup
      isOpen={isOpen}
      onClose={() => { if (!submitting) { onClose(); } }}
      title={<div className="flex items-center gap-2"><span role="img" aria-label="email">📧</span> Confirmation d'envoi des emails</div>}
      additionalClass="w-full max-w-4xl"
    >
      {rdv && (
        <div className="space-y-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="font-semibold text-blue-900"><span role="img" aria-label="inbox">📬</span> Emails qui seront envoyés</div>
            <p className="text-sm text-blue-900/80">Les emails de confirmation seront envoyés simultanément au client et au collaborateur.</p>
          </div>

          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            <div className="relative">
              <button
                type="button"
                title="Copier"
                aria-label="Copier le contenu client"
                onClick={() => onCopyHtml(clientPreviewRef)}
                className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs shadow-sm hover:bg-slate-50"
              >
                <span className="inline-block h-4 w-4" aria-hidden="true" role="img" aria-label="clipboard">📋</span>
                Copier
              </button>
              <div ref={clientPreviewRef}>
                {renderEmailPreviewForRdv(rdv, 'client')}
              </div>
            </div>
            <div className="relative">
              <button
                type="button"
                title="Copier"
                aria-label="Copier le contenu collaborateur"
                onClick={() => onCopyHtml(collabPreviewRef)}
                className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs shadow-sm hover:bg-slate-50"
              >
                <span className="inline-block h-4 w-4" aria-hidden="true" role="img" aria-label="clipboard">📋</span>
                Copier
              </button>
              <div ref={collabPreviewRef}>
                {renderEmailPreviewForRdv(rdv, 'collaborateur')}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
            <div className="mb-1 font-semibold text-sky-900"><span role="img" aria-label="clipboard">📋</span> Récapitulatif de l'envoi :</div>
            <ul className="list-disc pl-5 text-sm text-sky-900/90">
              <li><strong>Client :</strong> {rdv.contact.email || '—'}</li>
              <li><strong>Collaborateur :</strong> {rdv.assigneA.nom} ({rdv.assigneA.email || '—'})</li>
              <li><strong>Objet :</strong> Confirmation de rendez-vous d'estimation</li>
            </ul>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="mb-1 font-semibold text-amber-900"><span role="img" aria-label="warning">⚠️</span> Confirmation requise</div>
            <p className="text-sm text-amber-900/80">Voulez-vous vraiment envoyer ces emails de confirmation ? Cette action ne peut pas être annulée.</p>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-slate-700 disabled:opacity-60"
              onClick={() => { if (!submitting) { onClose(); } }}
              disabled={submitting}
            >Annuler</button>
            <button
              type="button"
              className={[
                'px-4 py-2 rounded-md text-white',
                submitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700',
              ].join(' ')}
              onClick={onConfirm}
              disabled={submitting}
            >{submitting ? 'Envoi en cours…' : (<><span role="img" aria-label="email">📧</span> Envoyer les emails</>)}</button>
          </div>
        </div>
      )}
    </Popup>
  );
}

export default EmailConfirmDialog;


